import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchAdminLeadBrokerConfig,
  patchAdminLeadBrokerConfig,
  testAdminLeadBrokerConnection,
} from "../../../../api/adminLeadBrokerApi";
import AdminPageHeader from "../layout/AdminPageHeader";
import AdminPageLayout from "../layout/AdminPageLayout";
import AdminStatePanel from "../layout/AdminStatePanel";
import RequirePermission from "../../../../auth/RequirePermission";
import StatusBadge from "../../intelligence/StatusBadge";
import { useAuth } from "../../../../auth/useAuth";
import "../../../../styles/adminLeadBroker.css";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "connection", label: "Connection" },
  { id: "workflow", label: "Workflow" },
  { id: "messaging", label: "Messaging" },
  { id: "compliance", label: "Compliance" },
];

const SETUP_STEPS = [
  {
    id: "deploy",
    title: "Run the Lead Broker API",
    detail:
      "Deploy the Lead Broker service (Postgres, Redis, workers). Midpoint submits leads to it; matching and WhatsApp run there.",
  },
  {
    id: "env",
    title: "Set server environment variables",
    detail:
      "On the Midpoint API server: LEAD_BROKER_INTERNAL_API_KEY, LEAD_BROKER_API_BASE_URL. On Lead Broker: matching INTERNAL_API_KEY.",
  },
  {
    id: "connect",
    title: "Test the connection",
    detail:
      "Use Test connection to confirm Midpoint can reach Lead Broker with your API key.",
  },
  {
    id: "enable",
    title: "Enable platform integration",
    detail:
      "Turn on submission from Midpoint and confirm API base URL and source identifier.",
  },
  {
    id: "client-views",
    title: "Configure each Client View",
    detail:
      "In Client View Studio → Lead Broker: enable the view, chatbot widget, and use cases (e.g. vehicle finance).",
  },
];

function TabNav({ activeTab, onChange }) {
  return (
    <nav className="admin-page-header__tabs" aria-label="Lead Broker sections">
      {TABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          className={`admin-page-header__tab${activeTab === tab.id ? " is-active" : ""}`}
          aria-current={activeTab === tab.id ? "page" : undefined}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </nav>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="lb-field">
      <span className="lb-field__label">{label}</span>
      {children}
      {hint ? <span className="lb-field__hint">{hint}</span> : null}
    </label>
  );
}

function StatusTile({ label, ok, detail }) {
  return (
    <div className={`lb-status-tile${ok ? " lb-status-tile--ok" : " lb-status-tile--issue"}`}>
      <div className="lb-status-tile__head">
        <span>{label}</span>
        <StatusBadge label={ok ? "OK" : "Issue"} tone={ok ? "confirmed" : "disputed"} />
      </div>
      {detail ? <p>{detail}</p> : null}
    </div>
  );
}

function AdminLeadBrokerConfigPageContent({ onClose, onNavigate }) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("users.manage");

  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState(null);
  const [lastTestOk, setLastTestOk] = useState(null);
  const [payload, setPayload] = useState(null);

  const [integrationForm, setIntegrationForm] = useState({
    isEnabled: false,
    apiBaseUrl: "",
    sourceIdentifier: "automapa",
    operatorConsoleUrl: "",
    notes: "",
  });

  const [workflowForm, setWorkflowForm] = useState({
    vendorReplyWindowHours: 48,
    leadShareConsentReplyWindowHours: 48,
    handoffClaimTokenExpiresHours: 72,
    publicHandoffClaimBaseUrl: "",
    dealerPortalBaseUrl: "",
  });

  const [whatsappForm, setWhatsappForm] = useState({
    vendorLeadAlertTemplate: "",
    leadShareConsentTemplate: "",
    vendorClaimLinkTemplate: "",
    leadVendorIntroTemplate: "",
    defaultLanguage: "es",
  });

  const [complianceForm, setComplianceForm] = useState({
    defaultPolicyVersion: "",
    optOutConfirmationEnabled: true,
    optOutConfirmationTemplate: "",
    optOutConfirmationDedupeHours: 24,
  });

  const showToast = (message, type = "info") => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 5000);
  };

  const hydrateForms = useCallback((body) => {
    const integration = body.integration || {};
    const effective = body.serviceConfig?.effective || {};

    setIntegrationForm({
      isEnabled: integration.isEnabled === true,
      apiBaseUrl: integration.apiBaseUrl || "",
      sourceIdentifier: integration.sourceIdentifier || "automapa",
      operatorConsoleUrl: integration.operatorConsoleUrl || "",
      notes: integration.notes || "",
    });

    setWorkflowForm({
      vendorReplyWindowHours: effective.workflow?.vendorReplyWindowHours ?? 48,
      leadShareConsentReplyWindowHours:
        effective.workflow?.leadShareConsentReplyWindowHours ?? 48,
      handoffClaimTokenExpiresHours: effective.workflow?.handoffClaimTokenExpiresHours ?? 72,
      publicHandoffClaimBaseUrl: effective.workflow?.publicHandoffClaimBaseUrl || "",
      dealerPortalBaseUrl: effective.workflow?.dealerPortalBaseUrl || "",
    });

    setWhatsappForm({
      vendorLeadAlertTemplate: effective.whatsapp?.vendorLeadAlertTemplate || "",
      leadShareConsentTemplate: effective.whatsapp?.leadShareConsentTemplate || "",
      vendorClaimLinkTemplate: effective.whatsapp?.vendorClaimLinkTemplate || "",
      leadVendorIntroTemplate: effective.whatsapp?.leadVendorIntroTemplate || "",
      defaultLanguage: effective.whatsapp?.defaultLanguage || "es",
    });

    setComplianceForm({
      defaultPolicyVersion: effective.compliance?.defaultPolicyVersion || "",
      optOutConfirmationEnabled: effective.compliance?.optOutConfirmationEnabled !== false,
      optOutConfirmationTemplate: effective.compliance?.optOutConfirmationTemplate || "",
      optOutConfirmationDedupeHours: effective.compliance?.optOutConfirmationDedupeHours ?? 24,
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await fetchAdminLeadBrokerConfig();
      setPayload(body);
      hydrateForms(body);
    } catch (error) {
      setPayload(null);
      const message = error.message || "Unable to load Lead Broker configuration.";
      if (message.toLowerCase().includes("route not found")) {
        setErrorMessage(
          "Lead Broker admin API is unavailable. Restart the Midpoint API server, then refresh.",
        );
      } else {
        setErrorMessage(message);
      }
    } finally {
      setLoading(false);
    }
  }, [hydrateForms]);

  useEffect(() => {
    load();
  }, [load]);

  const serviceStatus = payload?.serviceStatus;
  const effective = payload?.serviceConfig?.effective;
  const envSummary = payload?.envSummary || {};

  const statusTiles = useMemo(
    () => [
      {
        key: "health",
        label: "API reachable",
        ok: serviceStatus?.reachable,
        detail: serviceStatus?.reachable ? serviceStatus.baseUrl : "Cannot reach Lead Broker API",
      },
      {
        key: "database",
        label: "Postgres",
        ok: serviceStatus?.checks?.database?.ok,
      },
      {
        key: "redis",
        label: "Redis",
        ok: serviceStatus?.checks?.redis?.ok,
      },
      {
        key: "queues",
        label: "Job queues",
        ok: serviceStatus?.checks?.queues?.ok,
      },
      {
        key: "whatsapp",
        label: "WhatsApp config",
        ok:
          effective?.whatsapp?.metaConfigured ??
          serviceStatus?.checks?.whatsappConfig?.data?.configured,
        detail: effective?.whatsapp?.provider
          ? `Provider: ${effective.whatsapp.provider}`
          : undefined,
      },
    ],
    [effective, serviceStatus],
  );

  const readiness = useMemo(() => {
    const apiKeyOk = envSummary.apiKeyConfigured === true;
    const baseUrlOk = Boolean(integrationForm.apiBaseUrl?.trim());
    const apiReachable = serviceStatus?.reachable === true;
    const integrationEnabled = integrationForm.isEnabled === true;
    const connectionTested = lastTestOk === true;

    const items = [
      {
        id: "api-key",
        label: "Internal API key on Midpoint server",
        complete: apiKeyOk,
        tab: "connection",
      },
      {
        id: "base-url",
        label: "Lead Broker API base URL configured",
        complete: baseUrlOk,
        tab: "connection",
      },
      {
        id: "reachable",
        label: "Lead Broker API health checks passing",
        complete: apiReachable,
        tab: "overview",
      },
      {
        id: "test",
        label: "Connection test succeeded",
        complete: connectionTested,
        tab: "connection",
      },
      {
        id: "enabled",
        label: "Platform submission enabled",
        complete: integrationEnabled,
        tab: "connection",
      },
    ];

    const completeCount = items.filter((item) => item.complete).length;
    return { items, completeCount, total: items.length, ready: completeCount === items.length };
  }, [envSummary, integrationForm, serviceStatus, lastTestOk]);

  const handleSave = async () => {
    if (!canManage) return;
    setSaving(true);
    try {
      const body = await patchAdminLeadBrokerConfig({
        integration: integrationForm,
        serviceConfig: {
          workflow: {
            vendorReplyWindowHours: Number(workflowForm.vendorReplyWindowHours),
            leadShareConsentReplyWindowHours: Number(workflowForm.leadShareConsentReplyWindowHours),
            handoffClaimTokenExpiresHours: Number(workflowForm.handoffClaimTokenExpiresHours),
            publicHandoffClaimBaseUrl: workflowForm.publicHandoffClaimBaseUrl,
            dealerPortalBaseUrl: workflowForm.dealerPortalBaseUrl,
          },
          whatsapp: whatsappForm,
          compliance: {
            ...complianceForm,
            optOutConfirmationDedupeHours: Number(complianceForm.optOutConfirmationDedupeHours),
          },
        },
      });
      setPayload(body);
      hydrateForms(body);
      showToast("Lead Broker settings saved.", "success");
    } catch (error) {
      showToast(error.message || "Unable to save Lead Broker configuration.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const result = await testAdminLeadBrokerConnection();
      setLastTestOk(result.ok === true);
      if (result.ok) {
        showToast(result.message || "Connection test succeeded.", "success");
      } else {
        showToast(result.message || "Connection test failed.", "error");
      }
      await load();
    } catch (error) {
      setLastTestOk(false);
      showToast(error.message || "Connection test failed.", "error");
    } finally {
      setTesting(false);
    }
  };

  const openClientViews = () => {
    if (onNavigate) {
      onNavigate("client-views-admin");
    }
  };

  const headerActions = (
    <>
      <button
        type="button"
        className="admin-btn admin-btn--ghost"
        onClick={load}
        disabled={loading || saving}
      >
        Refresh
      </button>
      <button
        type="button"
        className="admin-btn admin-btn--ghost"
        onClick={handleTestConnection}
        disabled={loading || saving || testing}
      >
        {testing ? "Testing…" : "Test connection"}
      </button>
      {integrationForm.operatorConsoleUrl ? (
        <a
          className="admin-btn admin-btn--ghost"
          href={integrationForm.operatorConsoleUrl}
          target="_blank"
          rel="noreferrer"
        >
          Operator console
        </a>
      ) : null}
      {canManage ? (
        <button
          type="button"
          className="admin-btn"
          onClick={handleSave}
          disabled={loading || saving}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      ) : null}
    </>
  );

  return (
    <AdminPageLayout className="admin-lead-broker-page">
      <AdminPageHeader
        eyebrow="Midpoint Map · Platform"
        title="Lead Broker"
        description="Connect Midpoint to the Lead Broker service so buyer inquiries can be matched to vendors, notified via WhatsApp, and handed off to dealer portals."
        onClose={onClose}
        navigation={<TabNav activeTab={activeTab} onChange={setActiveTab} />}
        primaryActions={headerActions}
      />

      <div className="admin-page-body lb-page">
        {toast ? (
          <div className={`lb-toast lb-toast--${toast.type}`} role="status">
            {toast.message}
          </div>
        ) : null}

        {errorMessage ? (
          <AdminStatePanel
            variant="empty"
            message={errorMessage}
            onRetry={load}
            retryLabel="Retry"
          />
        ) : null}

        {payload?.serviceConfigError ? (
          <div className="lb-alert lb-alert--warning" role="status">
            Lead Broker service config could not be loaded: {payload.serviceConfigError}
          </div>
        ) : null}

        {loading ? (
          <AdminStatePanel variant="empty" message="Loading Lead Broker configuration…" />
        ) : null}

        {!loading && payload ? (
          <>
              {activeTab === "overview" ? (
                <>
                  <section className="lb-intro panel-card">
                    <h2>What this page does</h2>
                    <p>
                      Lead Broker is a separate service that receives leads from Midpoint (e.g. when a
                      buyer asks about a vehicle on AutoMapa), matches them to vendors, sends WhatsApp
                      messages, collects consent, and completes handoff to dealers.{" "}
                      <strong>This page wires Midpoint to that service</strong> — not per-view chatbot
                      placement or use cases (those live in Client View Studio).
                    </p>
                    <div className="lb-flow-diagram" aria-hidden="true">
                      <span>Buyer on Client View</span>
                      <span className="lb-flow-diagram__arrow">→</span>
                      <span>Midpoint backend</span>
                      <span className="lb-flow-diagram__arrow">→</span>
                      <span>Lead Broker API</span>
                      <span className="lb-flow-diagram__arrow">→</span>
                      <span>WhatsApp &amp; dealers</span>
                    </div>
                  </section>

                  <div className="lb-readiness panel-card">
                    <header className="lb-readiness__head">
                      <div>
                        <h2>Setup progress</h2>
                        <p>
                          {readiness.ready
                            ? "Platform integration looks ready. Configure individual Client Views in Studio."
                            : `${readiness.completeCount} of ${readiness.total} checks complete — follow the steps below.`}
                        </p>
                      </div>
                      <div className="lb-readiness__score">
                        <strong>{readiness.completeCount}/{readiness.total}</strong>
                        <span>checks</span>
                      </div>
                    </header>
                    <ul className="lb-checklist">
                      {readiness.items.map((item) => (
                        <li
                          key={item.id}
                          className={`lb-checklist__item${item.complete ? " is-complete" : ""}`}
                        >
                          <span className="lb-checklist__icon" aria-hidden="true">
                            {item.complete ? "✓" : "○"}
                          </span>
                          <button
                            type="button"
                            className="lb-checklist__label"
                            onClick={() => setActiveTab(item.tab)}
                          >
                            {item.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <section className="lb-setup panel-card">
                    <h2>Recommended setup order</h2>
                    <ol className="lb-setup-steps">
                      {SETUP_STEPS.map((step, index) => (
                        <li key={step.id}>
                          <span className="lb-setup-steps__num">{index + 1}</span>
                          <div>
                            <strong>{step.title}</strong>
                            <p>{step.detail}</p>
                            {step.id === "client-views" && onNavigate ? (
                              <button
                                type="button"
                                className="admin-btn admin-btn--ghost lb-setup-steps__action"
                                onClick={openClientViews}
                              >
                                Open Client Views admin
                              </button>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ol>
                  </section>

                  <section className="lb-status-section panel-card">
                    <h2>Service health</h2>
                    <p className="lb-muted">
                      Live checks against the Lead Broker API ({serviceStatus?.baseUrl || "—"}).
                    </p>
                    <div className="lb-status-grid">
                      {statusTiles.map((tile) => (
                        <StatusTile
                          key={tile.key}
                          label={tile.label}
                          ok={tile.ok}
                          detail={tile.detail}
                        />
                      ))}
                    </div>
                  </section>

                  <section className="lb-dev panel-card">
                    <h2>Environment flags (read-only)</h2>
                    <div className="lb-status-grid lb-status-grid--compact">
                      <StatusTile
                        label="Simulation tools"
                        ok={effective?.developer?.simulationToolsEnabled !== true}
                        detail={
                          effective?.developer?.simulationToolsEnabled
                            ? "Enabled — use only in non-production."
                            : "Disabled"
                        }
                      />
                      <StatusTile
                        label="Swagger docs"
                        ok
                        detail={effective?.developer?.swaggerEnabled ? "Enabled" : "Disabled"}
                      />
                      <StatusTile
                        label="WhatsApp mode"
                        ok={effective?.whatsapp?.testMode !== true}
                        detail={effective?.whatsapp?.testMode ? "Test mode" : "Live messaging"}
                      />
                    </div>
                  </section>
                </>
              ) : null}

              {activeTab === "connection" ? (
                <section className="lb-panel panel-card">
                  <header className="lb-panel__head">
                    <h2>Midpoint ↔ Lead Broker connection</h2>
                    <p>
                      Tell the Midpoint server where Lead Broker lives and whether to submit leads.
                      Secrets stay in environment variables — never paste API keys here.
                    </p>
                  </header>

                  <div className="lb-form-grid">
                    <div className="lb-toggle-row">
                      <label className="lb-toggle">
                        <input
                          type="checkbox"
                          checked={integrationForm.isEnabled}
                          disabled={!canManage || saving}
                          onChange={(event) =>
                            setIntegrationForm((current) => ({
                              ...current,
                              isEnabled: event.target.checked,
                            }))
                          }
                        />
                        <span>
                          <strong>Enable lead submission from Midpoint</strong>
                          <span className="lb-muted">
                            When on, eligible Client Views can forward inquiries to Lead Broker.
                          </span>
                        </span>
                      </label>
                    </div>

                    <Field
                      label="Lead Broker API base URL"
                      hint="Server-side URL Midpoint calls (e.g. http://localhost:4000 in dev)."
                    >
                      <input
                        type="url"
                        className="lb-input"
                        value={integrationForm.apiBaseUrl}
                        disabled={!canManage || saving}
                        onChange={(event) =>
                          setIntegrationForm((current) => ({
                            ...current,
                            apiBaseUrl: event.target.value,
                          }))
                        }
                      />
                    </Field>

                    <Field
                      label="Source identifier"
                      hint="Sent as source on every lead (e.g. automapa). Must match Lead Broker expectations."
                    >
                      <input
                        type="text"
                        className="lb-input"
                        value={integrationForm.sourceIdentifier}
                        disabled={!canManage || saving}
                        onChange={(event) =>
                          setIntegrationForm((current) => ({
                            ...current,
                            sourceIdentifier: event.target.value,
                          }))
                        }
                      />
                    </Field>

                    <Field
                      label="Operator console URL"
                      hint="Optional bookmark for ops — opens the Lead Broker operator UI."
                    >
                      <input
                        type="url"
                        className="lb-input"
                        value={integrationForm.operatorConsoleUrl}
                        disabled={!canManage || saving}
                        onChange={(event) =>
                          setIntegrationForm((current) => ({
                            ...current,
                            operatorConsoleUrl: event.target.value,
                          }))
                        }
                      />
                    </Field>

                    <Field
                      label="Internal API key"
                      hint="Set LEAD_BROKER_INTERNAL_API_KEY on the Midpoint server (read-only here)."
                    >
                      <input
                        type="text"
                        className="lb-input lb-input--readonly"
                        value={envSummary.apiKeyConfigured ? "Configured ✓" : "Not configured"}
                        disabled
                      />
                    </Field>

                    <Field label="Operator notes" hint="Internal runbook notes — not sent to Lead Broker.">
                      <textarea
                        className="lb-input"
                        rows={3}
                        value={integrationForm.notes}
                        disabled={!canManage || saving}
                        onChange={(event) =>
                          setIntegrationForm((current) => ({ ...current, notes: event.target.value }))
                        }
                      />
                    </Field>
                  </div>

                  <div className="lb-panel__footer">
                    <p className="lb-muted">
                      After updating URL or source, run <strong>Test connection</strong> in the header,
                      then save.
                    </p>
                  </div>
                </section>
              ) : null}

              {activeTab === "workflow" ? (
                <section className="lb-panel panel-card">
                  <header className="lb-panel__head">
                    <h2>Workflow timing &amp; URLs</h2>
                    <p>
                      How long vendors and buyers have to respond, and where claim links should point.
                      These settings are stored in Lead Broker and apply platform-wide.
                    </p>
                  </header>
                  <div className="lb-form-grid">
                    <Field
                      label="Vendor reply window (hours)"
                      hint="How long a matched vendor has to respond before the lead moves on."
                    >
                      <input
                        type="number"
                        min="1"
                        className="lb-input"
                        value={workflowForm.vendorReplyWindowHours}
                        disabled={!canManage || saving}
                        onChange={(event) =>
                          setWorkflowForm((current) => ({
                            ...current,
                            vendorReplyWindowHours: event.target.value,
                          }))
                        }
                      />
                    </Field>
                    <Field
                      label="Lead share consent window (hours)"
                      hint="Time allowed for buyer WhatsApp consent before expiring."
                    >
                      <input
                        type="number"
                        min="1"
                        className="lb-input"
                        value={workflowForm.leadShareConsentReplyWindowHours}
                        disabled={!canManage || saving}
                        onChange={(event) =>
                          setWorkflowForm((current) => ({
                            ...current,
                            leadShareConsentReplyWindowHours: event.target.value,
                          }))
                        }
                      />
                    </Field>
                    <Field
                      label="Handoff claim token expiry (hours)"
                      hint="Lifetime of dealer claim links sent via WhatsApp."
                    >
                      <input
                        type="number"
                        min="1"
                        className="lb-input"
                        value={workflowForm.handoffClaimTokenExpiresHours}
                        disabled={!canManage || saving}
                        onChange={(event) =>
                          setWorkflowForm((current) => ({
                            ...current,
                            handoffClaimTokenExpiresHours: event.target.value,
                          }))
                        }
                      />
                    </Field>
                    <Field
                      label="Public handoff claim base URL"
                      hint="HTTPS base for claim links in production (dealers open this to claim a lead)."
                    >
                      <input
                        type="url"
                        className="lb-input"
                        value={workflowForm.publicHandoffClaimBaseUrl}
                        disabled={!canManage || saving}
                        onChange={(event) =>
                          setWorkflowForm((current) => ({
                            ...current,
                            publicHandoffClaimBaseUrl: event.target.value,
                          }))
                        }
                      />
                    </Field>
                    <Field
                      label="Dealer portal base URL"
                      hint="AutoMapa dealer portal URL; handoff messages link here for login."
                    >
                      <input
                        type="url"
                        className="lb-input"
                        placeholder="https://your-site.example/v/automap/dealer"
                        value={workflowForm.dealerPortalBaseUrl}
                        disabled={!canManage || saving}
                        onChange={(event) =>
                          setWorkflowForm((current) => ({
                            ...current,
                            dealerPortalBaseUrl: event.target.value,
                          }))
                        }
                      />
                    </Field>
                  </div>
                </section>
              ) : null}

              {activeTab === "messaging" ? (
                <section className="lb-panel panel-card">
                  <header className="lb-panel__head">
                    <h2>WhatsApp templates</h2>
                    <p>
                      Template names registered with your WhatsApp provider (Meta). Provider credentials
                      are deployment config — update template names here without redeploying Lead Broker.
                    </p>
                    {effective?.whatsapp?.provider ? (
                      <p className="lb-muted">Current provider: {effective.whatsapp.provider}</p>
                    ) : null}
                  </header>
                  <div className="lb-form-grid">
                    {[
                      ["vendorLeadAlertTemplate", "Vendor lead alert", "Notifies a vendor about a new matched lead."],
                      ["leadShareConsentTemplate", "Lead share consent", "Asks the buyer to consent via WhatsApp."],
                      ["vendorClaimLinkTemplate", "Vendor claim link", "Sends the dealer their claim URL."],
                      ["leadVendorIntroTemplate", "Lead vendor intro", "Introduces buyer and vendor after consent."],
                    ].map(([field, label, hint]) => (
                      <Field key={field} label={label} hint={hint}>
                        <input
                          type="text"
                          className="lb-input"
                          value={whatsappForm[field]}
                          disabled={!canManage || saving}
                          onChange={(event) =>
                            setWhatsappForm((current) => ({ ...current, [field]: event.target.value }))
                          }
                        />
                      </Field>
                    ))}
                    <Field label="Default language code" hint="e.g. es for Spanish template variants.">
                      <input
                        type="text"
                        className="lb-input"
                        value={whatsappForm.defaultLanguage}
                        disabled={!canManage || saving}
                        onChange={(event) =>
                          setWhatsappForm((current) => ({
                            ...current,
                            defaultLanguage: event.target.value,
                          }))
                        }
                      />
                    </Field>
                  </div>
                </section>
              ) : null}

              {activeTab === "compliance" ? (
                <section className="lb-panel panel-card">
                  <header className="lb-panel__head">
                    <h2>Compliance defaults</h2>
                    <p>
                      Default policy version and opt-out messaging for WhatsApp outreach. Per-view consent
                      copy is configured in Client View Studio.
                    </p>
                  </header>
                  <div className="lb-form-grid">
                    <Field
                      label="Default policy version"
                      hint="Version string stored on leads for audit (e.g. 2026-06-25)."
                    >
                      <input
                        type="text"
                        className="lb-input"
                        value={complianceForm.defaultPolicyVersion}
                        disabled={!canManage || saving}
                        onChange={(event) =>
                          setComplianceForm((current) => ({
                            ...current,
                            defaultPolicyVersion: event.target.value,
                          }))
                        }
                      />
                    </Field>
                    <div className="lb-toggle-row">
                      <label className="lb-toggle">
                        <input
                          type="checkbox"
                          checked={complianceForm.optOutConfirmationEnabled}
                          disabled={!canManage || saving}
                          onChange={(event) =>
                            setComplianceForm((current) => ({
                              ...current,
                              optOutConfirmationEnabled: event.target.checked,
                            }))
                          }
                        />
                        <span>
                          <strong>Send opt-out confirmation on WhatsApp</strong>
                          <span className="lb-muted">
                            Confirms when a user opts out of further messages.
                          </span>
                        </span>
                      </label>
                    </div>
                    <Field label="Opt-out confirmation template" hint="WhatsApp template name for opt-out ack.">
                      <input
                        type="text"
                        className="lb-input"
                        value={complianceForm.optOutConfirmationTemplate}
                        disabled={!canManage || saving}
                        onChange={(event) =>
                          setComplianceForm((current) => ({
                            ...current,
                            optOutConfirmationTemplate: event.target.value,
                          }))
                        }
                      />
                    </Field>
                    <Field
                      label="Opt-out dedupe window (hours)"
                      hint="Minimum time between duplicate opt-out confirmations to the same number."
                    >
                      <input
                        type="number"
                        min="1"
                        className="lb-input"
                        value={complianceForm.optOutConfirmationDedupeHours}
                        disabled={!canManage || saving}
                        onChange={(event) =>
                          setComplianceForm((current) => ({
                            ...current,
                            optOutConfirmationDedupeHours: event.target.value,
                          }))
                        }
                      />
                    </Field>
                  </div>
                </section>
              ) : null}
            </>
        ) : null}
      </div>
    </AdminPageLayout>
  );
}

export default function AdminLeadBrokerConfigPage(props) {
  return (
    <RequirePermission
      permission="users.manage"
      fallbackMessage="You do not have permission to manage Lead Broker configuration."
    >
      <AdminLeadBrokerConfigPageContent {...props} />
    </RequirePermission>
  );
}
